import React, { useEffect, useState } from 'react';
import style from './style.module.css';
import conditional from '../../utils/conditional';
import platform from 'geeky-platform';
import api from '../../api';

const baseLink = 'https://activemarion.app/l/';

const rewardImage = thumbnail => ({ backgroundImage: `url('${thumbnail || ''}')` });

const Rewards = props => {
    const [, setVisible] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [redeemVisible, setRedeemVisible] = useState(false);
    const [selectedReward, setSelectedReward] = useState(null);
    const [claiming, setClaiming] = useState(false);
    const [redeeming, setRedeeming] = useState(false);
    const [countdown, setCountdown] = useState(15);

    useEffect(() => {
        if (!props.shareData) return;
        setVisible(true);
    }, [props.shareData]);

    const share = service => {
        const link = props.shareData || {};
        let message = '';

        if (link.rank) {
            message = `I'm #${link.rank.rank} in ${link.rank.name} on Active Marion Project!\n${baseLink}${link.code}`;
        }

        if (link.goal) {
            const percent = Math.round(link.goal.percentage * 100);
            message = `I reached my ${link.goal.time} ${link.goal.type} goal by ${percent}%!\n${baseLink}${link.code}`;
        }

        if (link.badge) {
            message = `I earned the ${link.badge.name} badge!\n${baseLink}${link.code}`;
        }

        platform.request({ id: 'shareApp', data: { service, message } });
    };

    const claim = async () => {
        setClaiming(true);
        await api.rewards.claim(selectedReward.group._id, selectedReward._id);
        await props.onReload();
        setDialogVisible(false);
        setTimeout(() => {
            setSelectedReward(null);
            setClaiming(false);
        }, 600);
    };

    const waitForRedemption = () => new Promise(resolve => setTimeout(resolve, 15000));

    const redeem = async () => {
        setClaiming(true);
        await api.rewards.redeem(selectedReward._id);
        setRedeeming(true);
        setCountdown(15);
        await props.onReload();

        const timer = setInterval(() => setCountdown(value => value - 1), 1000);
        await waitForRedemption();
        clearInterval(timer);

        setRedeemVisible(false);
        setTimeout(() => {
            setSelectedReward(null);
            setClaiming(false);
            setRedeeming(false);
        }, 600);
    };

    const openClaim = data => {
        if (data.status !== 'eligible') return;
        setSelectedReward(data);
        setDialogVisible(true);
    };

    const openRedeem = data => {
        setSelectedReward(data);
        setRedeemVisible(true);
    };

    const availableCard = data => {
        const eligible = data.status === 'eligible';
        return (
            <button
                className={`${style.rewardCard} ${eligible ? style.eligible : style.locked}`}
                key={data._id}
                onClick={() => openClaim(data)}
                type="button"
                disabled={!eligible}
            >
                <span className={style.imageWrap}>
                    <span className={style.rewardImage} style={rewardImage(data.secondaryInfo.thumbnail)} />
                    <span className={style.statusPill}><span className={style.statusDot} />{eligible ? 'Eligible' : 'Keep going'}</span>
                </span>
                <span className={style.cardCopy}>
                    <span className={style.company}>{data.secondaryInfo.company || 'AMP Rewards'}</span>
                    <span className={style.title}>{data.name}</span>
                </span>
                <span className={style.cardArrow} aria-hidden="true">→</span>
            </button>
        );
    };

    const earnedCard = data => (
        <button className={`${style.rewardCard} ${style.earned}`} key={data._id} onClick={() => openRedeem(data)} type="button">
            <span className={style.imageWrap}>
                <span className={style.rewardImage} style={rewardImage(data.choice.secondaryInfo.thumbnail)} />
                <span className={`${style.statusPill} ${data.redeemed ? style.redeemed : ''}`}>
                    <span className={style.statusDot} />{data.redeemed ? 'Redeemed' : 'Available'}
                </span>
            </span>
            <span className={style.cardCopy}>
                <span className={style.company}>{data.choice.secondaryInfo.company || 'AMP Rewards'}</span>
                <span className={style.title}>{data.choice.name}</span>
            </span>
            <span className={style.cardArrow} aria-hidden="true">→</span>
        </button>
    );

    const progressCard = data => (
        <div className={style.progressCard} key={data._id}>
            <div className={style.progressImage} style={rewardImage(data.secondaryInfo.thumbnail)} />
            <div className={style.company}>{data.secondaryInfo.company || 'AMP Rewards'}</div>
            <div className={style.title}>{data.name}</div>
        </div>
    );

    const section = data => (
        <section className={style.section} key={data._id || data.name}>
            <header className={style.header}>
                <div>
                    <div className={style.eyebrow}>Reward collection</div>
                    <h2 className={style.sectionTitle}>{data.name}</h2>
                </div>
                <span className={style.rewardCount}>{(data.choices || []).length}</span>
            </header>
            <div className={style.content}>
                {(data.choices || []).map(choice => ({ ...choice, group: data })).map(availableCard)}
                <div className={style.cardSpacer} />
            </div>
        </section>
    );

    const usedCount = ((selectedReward || {}).viewDates || []).length;
    const progressRewards = (((selectedReward || {}).group || {}).choices || [])
        .filter(choice => choice._id !== (selectedReward || {})._id);
    const selectedChoice = (selectedReward || {}).choice || {};
    const selectedBrand = selectedChoice.secondaryInfo || {};

    const rewardsClass = conditional('Rewards', style, { visible: props.visible });
    const confirmClass = conditional('confirmDialog', style, { visible: dialogVisible });
    const redeemClass = conditional('redeemDialog', style, { visible: redeemVisible });
    const actionsClass = conditional('confirmActions', style, { disabled: claiming });
    const redeemActionsClass = conditional('redeemActions', style, { disabled: claiming });
    const redeemContainer = conditional('redeemContainer', style, { visible: !redeeming });
    const redeemingContainer = conditional('redeemingContainer', style, { visible: redeeming });
    const redeemMessage = conditional('redeemMessage', style, { alert: usedCount > 0 });
    const redeemCodeClass = conditional('redeemCode', style, { animating: redeeming });

    return (
        <div className={rewardsClass} onClick={props.onFinish}>
            <nav className={style.nav} onClick={event => event.stopPropagation()}>
                <div>

                </div>
                <button className={style.navClose} onClick={props.onFinish} type="button" aria-label="Close rewards">×</button>
            </nav>

            <main className={style.container} onClick={event => event.stopPropagation()}>
                {props.available && props.available.length ? (
                    <section className={`${style.section} ${style.claimedSection}`}>
                        <header className={style.header}>
                            <div>
                                <div className={style.eyebrow}>Ready when you are</div>
                                <h2 className={style.sectionTitle}>Claimed rewards</h2>
                            </div>
                            <span className={style.rewardCount}>{props.available.length}</span>
                        </header>
                        <div className={style.content}>
                            {props.available.map(choice => ({ ...choice, group: {} })).map(earnedCard)}
                            <div className={style.cardSpacer} />
                        </div>
                    </section>
                ) : null}
                {(props.rewards || []).map(section)}
            </main>

            <div className={confirmClass} onClick={event => event.stopPropagation()}>
                <div className={style.dialogCard}>
                    <div className={style.heroIcon} style={rewardImage(((selectedReward || {}).secondaryInfo || {}).thumbnail)} />
                    <span className={style.dialogEyebrow}>Reward unlocked</span>
                    <div className={style.confirmMessage}>Right on, you earned <strong>{(selectedReward || {}).name}</strong> from <strong>{((selectedReward || {}).secondaryInfo || {}).company}</strong>!{progressRewards.length ? ' If you cash out now, you won\'t be able to earn these rewards.' : ' Claim your reward for your hard work!'}</div>
                    <div className={style.confirmPreview} style={{ display: progressRewards.length ? 'flex' : 'none' }}>
                        {progressRewards.map(progressCard)}
                        <div className={style.cardSpacer} />
                    </div>
                    <div className={actionsClass}>
                        <button className={style.secondaryButton} onClick={() => setDialogVisible(false)} type="button">{progressRewards.length ? 'Keep going' : 'Cancel'}</button>
                        <button className={style.primaryButton} onClick={claim} type="button">Claim reward</button>
                    </div>
                </div>
            </div>

            <div className={redeemClass} onClick={event => event.stopPropagation()}>
                <div className={`${style.dialogCard} ${style.redeemDialogCard}`}>
                    <div className={redeemContainer}>
                        <div className={style.heroIcon} style={rewardImage(selectedBrand.thumbnail)} />
                        <span className={style.dialogEyebrow}>Redeem in person</span>
                        <div className={style.redeemMessage}>Show someone at <strong>{selectedBrand.company}</strong> your phone, then tap the green button. The reward will be marked as redeemed permanently.</div>
                        <div className={redeemActionsClass}>
                            <button className={style.secondaryButton} onClick={() => setRedeemVisible(false)} type="button">Not yet</button>
                            <button className={style.primaryButton} onClick={redeem} type="button">I'm ready</button>
                        </div>
                    </div>
                    <div className={redeemingContainer}>
                        <div className={style.heroIcon} style={rewardImage(selectedBrand.thumbnail)} />
                        <span className={style.dialogEyebrow}>Redemption code</span>
                        <div className={redeemCodeClass}>{(selectedReward || {}).code}</div>
                        <div className={redeemMessage}>{usedCount > 0 ? `This code was attempted ${usedCount} times before.` : 'This code has not been redeemed before.'}</div>
                        <div className={style.redeemCounter}>{countdown}s</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Rewards;
